/* ============================================================================
   socialAuth - thin wrapper around @capgo/capacitor-social-login.
   ----------------------------------------------------------------------------
   Initializes the SocialLogin plugin with our Google + Apple OAuth client IDs
   and exposes simple loginWithGoogle / loginWithApple helpers that return the
   ID token plus user info we need to forward to our backend.

   The same module works on web and native because Capacitor's web fallback
   uses the standard Google Identity Services script. On iOS / Android the
   plugin uses the native Google Sign-In SDK + Apple AuthenticationServices.

   IDs sourced from Google Cloud Console under the "Nexus Dex" project.
   ============================================================================ */
import { SocialLogin } from "@capgo/capacitor-social-login";
import { Capacitor } from "@capacitor/core";

// Google Cloud Console - Nexus Dex project OAuth client IDs.
//   iOS Client ID is bound to the bundle id com.nexusdex.app
//   Web Client ID is the "server client id" used to verify ID tokens on the
//   backend. We pass it as iOSServerClientId so iOS sign-in returns an
//   audience-correct ID token our server can validate.
// Nexus Dex Production project (project number 481643925958).
// Previous IDs (721092093399-...) pointed at the locked-out
// nexus.dexai@gmail.com project and returned 401 disabled_client.
const IOS_CLIENT_ID =
  "481643925958-ie4cdl0lt9oco8du76nb4g1260j5t9mv.apps.googleusercontent.com";
const WEB_CLIENT_ID =
  "481643925958-ccndgdonk0m0nn69rdsufren7u0hun9p.apps.googleusercontent.com";

// Apple Sign In service ID (for now we use the bundle id directly since we
// have not registered a separate Service ID; iOS only needs the bundle id at
// initialize time to know which provider to wire up).
const APPLE_CLIENT_ID = "com.nexusdex.app";

let initPromise: Promise<void> | null = null;

/**
 * Options we hand to SocialLogin.initialize(). Derived from the plugin's own
 * signature so a shape change surfaces at compile time.
 */
type InitOptions = Parameters<typeof SocialLogin.initialize>[0];

/** Initialize the SocialLogin plugin once. Subsequent calls are no-ops. */
function ensureInit(): Promise<void> {
  if (initPromise) return initPromise;

  const options: InitOptions = {
    google: {
      iOSClientId: IOS_CLIENT_ID,
      iOSServerClientId: WEB_CLIENT_ID,
      webClientId: WEB_CLIENT_ID,
      mode: "online",
    },
  };

  // ONLY register Apple where it can actually work. Android's native
  // initialize() validates the apple block FIRST and rejects the ENTIRE call —
  // before it ever reaches the google block — when apple.redirectUrl is missing
  // (SocialLoginPlugin.java: `call.reject("apple.android.redirectUrl is null or
  // empty")`). We have no Apple Services ID or redirect URL to give it, see
  // isAppleSignInAvailable() below. Passing apple anyway therefore took GOOGLE
  // sign-in down with it on Android: ensureInit() rejected, and the Google
  // button surfaced Apple's error message verbatim.
  //
  // Hiding the Apple button (2026-08-05) did not fix this, because the button
  // is not what triggers initialize() — the Google button does.
  if (isAppleSignInAvailable()) {
    options.apple = { clientId: APPLE_CLIENT_ID };
  }

  initPromise = SocialLogin.initialize(options).catch((err) => {
    // Reset so a future retry can re-initialize.
    initPromise = null;
    throw err;
  });
  return initPromise;
}

export interface SocialLoginResult {
  idToken: string;
  email: string | null;
  displayName: string | null;
  subject: string | null;
}

/**
 * Sign in with Google. Returns the Google ID token + basic profile fields.
 *
 * DO NOT PASS `scopes` HERE. Email and profile are granted by default on both
 * platforms, and asking for them by name costs us the whole sign-in on Android:
 *
 *   if (scopesArray != null) {
 *     if (!(this.activity instanceof ModifiedMainActivityForSocialLoginPlugin)) {
 *       call.reject("You CANNOT use scopes without modifying the main activity.
 *                    Please follow the docs!");
 *
 * That check fires on the PRESENCE of the array, whatever is in it. The option
 * exists for INCREMENTAL authorization — extra Google API scopes (Drive,
 * Calendar) obtained through AuthorizationClient, which needs an onActivityResult
 * hook in MainActivity. We want none of that; we want an ID token.
 *
 * Nothing is lost by omitting it. Android's GoogleProvider seeds every request
 * with userinfo.email + userinfo.profile + openid regardless, and the sign-in
 * request it builds never reads the scopes array at all. iOS falls back to
 * defaultGrantedScopes = ["email", "profile", "openid"], a superset of what we
 * used to ask for.
 */
export async function loginWithGoogle(): Promise<SocialLoginResult> {
  await ensureInit();
  const res = await SocialLogin.login({
    provider: "google",
    options: {},
  });
  // Plugin returns either { result: { idToken, profile, ... } } on native or
  // a slightly different shape on web. We normalize.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r: any = (res as any).result ?? res;
  const idToken: string | undefined = r.idToken;
  if (!idToken) {
    throw new Error(
      "Google did not return an ID token. Please try again or use email sign-in.",
    );
  }
  const profile = r.profile ?? {};
  return {
    idToken,
    email: profile.email ?? r.email ?? null,
    displayName: profile.name ?? profile.givenName ?? r.displayName ?? null,
    subject: profile.id ?? r.userId ?? null,
  };
}

/** Sign in with Apple. Returns the Apple ID token + basic profile fields. */
export async function loginWithApple(): Promise<SocialLoginResult> {
  // Unreachable through the UI (every render site gates on
  // isAppleSignInAvailable), but ensureInit() no longer registers the Apple
  // provider on Android, so a stray call would otherwise fail deep inside the
  // plugin with an opaque "provider not found".
  if (!isAppleSignInAvailable()) {
    throw new Error(
      "Sign in with Apple is not available on Android. Please use Google or email sign-in.",
    );
  }
  await ensureInit();
  const res = await SocialLogin.login({
    provider: "apple",
    options: {
      scopes: ["email", "name"],
    },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r: any = (res as any).result ?? res;
  const idToken: string | undefined = r.idToken;
  if (!idToken) {
    throw new Error(
      "Apple did not return an ID token. Please try again or use email sign-in.",
    );
  }
  // Apple only returns the user's name on FIRST sign-in; after that it returns
  // null. We send what we have and let the backend persist it once.
  const givenName = r.givenName ?? r.profile?.givenName ?? "";
  const familyName = r.familyName ?? r.profile?.familyName ?? "";
  const displayName = [givenName, familyName].filter(Boolean).join(" ").trim();
  return {
    idToken,
    email: r.email ?? r.profile?.email ?? null,
    displayName: displayName || null,
    subject: r.user ?? r.profile?.user ?? null,
  };
}

/** Returns true when running inside a native iOS or Android shell. */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Whether "Continue with Apple" can actually complete on this platform.
 *
 * Also decides whether ensureInit() passes an `apple` block to the plugin at
 * all — on Android that block is fatal to the whole initialize() call, Google
 * included.
 *
 * FALSE ON ANDROID. Apple Sign In there is a web redirect flow that needs a
 * registered Apple **Services ID** plus a redirect URL — not a bundle id. We
 * pass APPLE_CLIENT_ID (the bundle id) and no redirectUrl, because no Services
 * ID has ever been registered, so every Android attempt fails after the user
 * has committed to it. A button that looks supported and then dies is worse
 * than no button: it reads as "the app is broken" rather than "use Google".
 *
 * Apple's Guideline 4.8 (offer Sign in with Apple alongside other social
 * logins) applies to iOS only, and Play has no equivalent rule, so hiding it
 * here carries no store risk.
 *
 * Re-enabling on Android is a real project, not a flag flip: register a
 * Services ID, host the redirect, pass both here, and verify the returned
 * token's `aud` still matches what verifyAppleIdToken() accepts on the server
 * (it currently checks APPLE_BUNDLE_ID, which a Services ID flow would NOT
 * satisfy). Until then this stays false.
 */
export function isAppleSignInAvailable(): boolean {
  return Capacitor.getPlatform() !== "android";
}
